import { useEffect, useState } from "react";
import axios from "axios";
import { defaultHeaders } from "../../Redux/Constants/DefaultHeaders";
import Pen from "../../Assets/pen.svg";
import {
	Space,
	ConfigProvider,
	Form,
	InputNumber,
	Button,
	Popconfirm,
	notification,
	Input,
	Checkbox,
} from "antd";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { logout } from "../../utils/index.js";
import CustomTable from "./CustomTable.js";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function DepositRecords() {
	const [notificationPopup, contextHolder] = notification.useNotification();
	const [form] = Form.useForm();
	const [sortedInfo, setSortedInfo] = useState([]);
	const [filteredInfo, setFilteredInfo] = useState({
		verified: [false],
	});
	const isEditing = (record) => record.key === editingKey;
	const [verified, setVerified] = useState(false);
	const [loading, setLoading] = useState(false);
	const [editingKey, setEditingKey] = useState("");
	const [tableEntries, setTableEntries] = useState([]);
	const [reloadTable, setReloadTable] = useState(false);
	const [openVerifyPopconfirm, setOpenVerifyPopconfirm] = useState(false);
	const [showVerifyPopconfirm, setShowVerifyPopconfirm] = useState(true);

	useEffect(() => {
		const originData = [];
		const fetchData = async () => {
			setLoading(true);
			let recordId = null;
			try {
				const requestData = await axios.request({
					method: "GET",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/reconciliations`,
				});
				if (!requestData.data.data) {
					setLoading(false);
				} else {
					let errCounter = 0;
					for (let data of requestData.data.data.requests) {
						try {
							recordId = data.id;
							originData.push({
								key: data.id,
								requestId: data.id,
								bankingCore: requestData.data.data.banking_core_options.find(
									(option) => option.id === data.banking_core,
								)?.name,
								statedAmt: data.stated_amt,
								actualAmt: data.actual_amt,
								creditGiven: data.credit_given === 1 ? true : false,
								safeName: data.safe_name,
								dateOfSubmission: dayjs
									.utc(data.date_of_submission, "YYYY-MM-DD HH:mm:ss")
									.tz(data.timezone)
									.format("YYYY-MM-DD hh:mm A z"),
								timezone: data.timezone,
								verified: data.verified === 1 ? true : false,
							});
						} catch (err) {
							errCounter++;
							console.log(`error for request: ${data.id}`, err.message, data);
						}
					}
					console.log("num of errors: ", errCounter);
					setTableEntries(originData);
					setLoading(false);
				}
			} catch (e) {
				if (e?.response?.data?.status === 401) {
					logout();
				} else {
					notificationPopup.error({
						message: `Request id: ${recordId} encountered the following error`,
						description: e.message,
						placement: "top",
						showProgress: true,
						pauseOnHover: true,
					});
				}
				console.log(e);
				setLoading(false);
			}
		};
		fetchData();
	}, [reloadTable, notificationPopup]);

	const edit = (record) => {
		setVerified(record.verified);
		form.setFieldsValue({
			...record,
			actualAmt: record.actualAmt ? record.actualAmt : record.statedAmt,
		});
		setEditingKey(record.key);
	};
	const cancel = () => {
		setEditingKey("");
	};

	const save = async (key) => {
		try {
			const row = await form.validateFields();
			setLoading(true);
			let res;
			// Update parent table
			const updatedParent = tableEntries.map((item) =>
				item.key === key ? { ...item, ...row } : item,
			);
			setTableEntries(updatedParent);
			const requestPayload = {
				actualAmt: `${row.actualAmt}`.includes("$")
					? row.actualAmt
					: `$${Number(row.actualAmt).toLocaleString("en-US", {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}`,
				verified: row.verified,
			};
			res = await axios.request({
				method: "PUT",
				headers: { ...defaultHeaders(), id: key },
				url: `${process.env.REACT_APP_API_URL}api/admin/reconciliations/${
					tableEntries.find((item) => item.key === key)?.requestId
				}`,
				data: requestPayload,
			});

			if (!res.data.error) {
				setReloadTable(!reloadTable);
				setEditingKey("");
			} else {
				notificationPopup.error({
					message: res.data.message,
					placement: "top",
					showProgress: true,
					pauseOnHover: true,
				});
			}
			setLoading(false);
		} catch (errInfo) {
			setLoading(false);
			if (errInfo.response.data.status === 401) {
				logout();
			}
			notificationPopup.error({
				message: "Validation Failed",
				description: JSON.stringify(errInfo?.response?.data?.data),
				placement: "top",
				showProgress: true,
				pauseOnHover: true,
			});
			console.log("Validate Failed:", errInfo);
		}
	};

	const handleVerifyOpenChange = (Opening) => {
		//Handle closing popup confirm logic clean up
		if (!Opening) {
			setOpenVerifyPopconfirm(Opening);
			return;
		}

		if (showVerifyPopconfirm) {
			setOpenVerifyPopconfirm(true);
		} else {
			save(editingKey);
		}
	};

	const tableColumns = [
		{
			title: "Request ID",
			dataIndex: "requestId",
			key: "requestId",
			sortOrder: sortedInfo.columnKey === "requestId" ? sortedInfo.order : null,
			sorter: (a, b) => a.requestId - b.requestId,
			align: "center",
		},
		{
			title: "Safe Name",
			dataIndex: "safeName",
			key: "safeName",
			//sorter: (a, b) => a.safeName.localeCompare(b.safeName),
			align: "center",
			filters: tableEntries
				.map((item) => item.safeName)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.safeName || null,
			onFilter: (value, record) => record.safeName.startsWith(value),
		},
		{
			title: "Stated Amount",
			dataIndex: "statedAmt",
			key: "statedAmt",
			align: "center",
		},
		{
			title: "Actual Amount",
			dataIndex: "actualAmt",
			key: "actualAmt",
			editable: true,
			filters: [{ text: "Empty", value: null }],
			filteredValue: filteredInfo.actualAmt || null,
			onFilter: (value, record) => record.actualAmt === value,
			align: "center",
		},
		{
			title: "Banking Core",
			dataIndex: "bankingCore",
			key: "bankingCore",
			align: "center",
		},
		{
			title: "Verified",
			dataIndex: "verified",
			key: "verified",
			editable: true,
			filters: [
				{ text: "True", value: true },
				{ text: "False", value: false },
			],
			filteredValue: filteredInfo.verified || null,
			onFilter: (value, record) => record.verified === value,
			align: "center",
		},
		{
			title: "Provisional Credit Given",
			dataIndex: "creditGiven",
			key: "creditGiven",
			filters: [
				{ text: "True", value: true },
				{ text: "False", value: false },
			],
			filteredValue: filteredInfo.creditGiven || null,
			onFilter: (value, record) => record.creditGiven === value,
			align: "center",
		},

		{
			title: "Date of Submission",
			dataIndex: "dateOfSubmission",
			key: "dateOfSubmission",
			align: "center",
		},
		{
			title: "Timezone",
			dataIndex: "timezone",
			key: "timezone",
			align: "center",
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			render: (_, record) => {
				return isEditing(record) ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<ConfigProvider
							theme={{
								token: {
									colorBgContainer: "#bcff00",
									colorBgElevated: "#333333",
								},
							}}
						>
							<Popconfirm
								title="Looks like the verify column hasn't been checked, if you've received the money, would you like to proceed?"
								onCancel={() => {}}
								cancelText="No"
								okText="Yes"
								onConfirm={() => save(record.key)}
								okButtonProps={{ type: "default" }}
								onOpenChange={handleVerifyOpenChange}
								open={openVerifyPopconfirm}
							>
								<Button
									style={{
										marginInlineEnd: 8,
									}}
								>
									Save
								</Button>
							</Popconfirm>

							<Popconfirm
								title="Are you sure?"
								onCancel={() => {}}
								cancelText="No"
								okText="Yes"
								onConfirm={() => cancel(record.key)}
								okButtonProps={{ type: "default" }}
							>
								<Button
									style={{
										marginInlineEnd: 8,
									}}
								>
									Cancel
								</Button>
							</Popconfirm>
						</ConfigProvider>
					</div>
				) : (
					<Space size="middle">
						<button
							style={{ backgroundColor: "transparent" }}
							onClick={() => edit(record)}
						>
							<img src={Pen} alt="Edit Button" />
						</button>
					</Space>
				);
			},
			align: "center",
		},
	];

	/**
	 * @param {Object} props - Props passed to the editable cell.
	 * @param {boolean} props.editing - Whether this cell is currently being edited.
	 * @param {string} props.dataIndex - The column dataIndex of this cell.
	 * @param {string} props.title - The column title of this cell.
	 * @param {Object} props.record - The table row data for this cell.
	 * @param {React.ReactNode} props.children - The default content of the cell.
	 * @param {boolean} [props.isExpandedTable=false] - Whether this cell is inside an expanded table.
	 * @param {Object} [props.restProps] - Any additional props passed down to the cell.
	 * @returns {JSX.Element} The rendered editable cell.
	 */
	const EditableCell = ({
		editing,
		dataIndex,
		title,
		record,
		children,
		isExpandedTable = false,
		...restProps
	}) => {
		const booleanFields = ["creditGiven", "verified"];
		const numberFields = ["actualAmt"];
		let tdStyle = { textAlign: "center" };

		if (!restProps.editable) {
			let value;
			if (booleanFields.includes(dataIndex)) {
				value = record[dataIndex] ? "True" : "False";
			} else {
				value = children;
			}
			return <td {...restProps}>{value}</td>;
		}

		let displayValue = children;

		if (!editing) {
			if (booleanFields.includes(dataIndex)) {
				displayValue = record[dataIndex] ? "True" : "False";
			} else if (dataIndex === "actualAmt") {
				displayValue = record[dataIndex];

				if (record[dataIndex] !== record.statedAmt) {
					tdStyle.color = "red";
				} else {
					tdStyle.color = "#bcff00";
				}
			}
		}
		// Determine validation rules
		let rules = [];
		if (!isExpandedTable) {
			rules.push({ required: true, message: `Please input ${title}!` });
		}
		let inputNode;

		if (numberFields.includes(dataIndex)) {
			inputNode = <InputNumber controls={false} disabled={verified} />;
		} else if (booleanFields.includes(dataIndex)) {
			inputNode = (
				<Checkbox
					onChange={(e) => {
						setVerified(e.target.checked);
						setShowVerifyPopconfirm(!e.target.checked);
					}}
				/>
			);
		} else {
			inputNode = <Input.TextArea autoSize style={{ padding: "4px" }} />;
		}

		return (
			<td {...restProps} style={tdStyle}>
				{editing ? (
					<Form.Item
						key={`${record.key}-${dataIndex}`}
						name={dataIndex}
						valuePropName={
							booleanFields.includes(dataIndex) ? "checked" : "value"
						}
						style={{ margin: 0 }}
						rules={rules}
						initialValue={record[dataIndex]}
					>
						{inputNode}
					</Form.Item>
				) : (
					displayValue
				)}
			</td>
		);
	};

	return (
		<CustomTable
			tableTitle="Deposit Records"
			form={form}
			setEditingKey={setEditingKey}
			reloadTable={reloadTable}
			setReloadTable={setReloadTable}
			tableEntries={tableEntries}
			tableColumns={tableColumns}
			loading={loading}
			cancel={cancel}
			contextHolder={contextHolder}
			setSortedInfo={setSortedInfo}
			setFilteredInfo={setFilteredInfo}
			EditableCell={EditableCell}
			isEditing={isEditing}
		/>
	);
}
